import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from '@/components/ui/sonner';
import '@/App.css';

// Public pages
import HomePage from '@/pages/public/HomePage';
import ServicesPage from '@/pages/public/ServicesPage';
import PricingPage from '@/pages/public/PricingPage';
import PortfolioPage from '@/pages/public/PortfolioPage';
import ContactPage from '@/pages/public/ContactPage';
import LoginPage from '@/pages/auth/LoginPage';
import AdminLoginPage from '@/pages/auth/AdminLoginPage';
import VerifyMagicLinkPage from '@/pages/auth/VerifyMagicLinkPage';

// Client portal
import ClientDashboard from '@/pages/client/ClientDashboard';
import ClientPlan from '@/pages/client/ClientPlan';
import ClientRequests from '@/pages/client/ClientRequests';
import ClientRequestDetail from '@/pages/client/ClientRequestDetail';
import ClientInvoices from '@/pages/client/ClientInvoices';
import ClientFiles from '@/pages/client/ClientFiles';
import ClientNotifications from '@/pages/client/ClientNotifications';

// Admin portal
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminClients from '@/pages/admin/AdminClients';
import AdminRequests from '@/pages/admin/AdminRequests';
import AdminPayments from '@/pages/admin/AdminPayments';
import AdminWebsiteEditor from '@/pages/admin/AdminWebsiteEditor';
import AdminUsers from '@/pages/admin/AdminUsers';

// Auth context
import { AuthProvider } from '@/context/AuthContext';
import ProtectedRoute from '@/components/ProtectedRoute';

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/services" element={<ServicesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/portfolio" element={<PortfolioPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/admin/login" element={<AdminLoginPage />} />
            <Route path="/verify-magic-link" element={<VerifyMagicLinkPage />} />

            {/* Client portal (client only) */}
            <Route
              path="/client/dashboard"
              element={
                <ProtectedRoute role={['client']}>
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/plan"
              element={
                <ProtectedRoute role={['client']}>
                  <ClientPlan />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/requests"
              element={
                <ProtectedRoute role={['client']}>
                  <ClientRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/requests/:id"
              element={
                <ProtectedRoute role={['client']}>
                  <ClientRequestDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/invoices"
              element={
                <ProtectedRoute role={['client']}>
                  <ClientInvoices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/files"
              element={
                <ProtectedRoute role={['client']}>
                  <ClientFiles />
                </ProtectedRoute>
              }
            />
            <Route
              path="/client/notifications"
              element={
                <ProtectedRoute role={['client']}>
                  <ClientNotifications />
                </ProtectedRoute>
              }
            />

            {/* Admin portal */}
            {/* General staff access: admin + developer + outreach */}
            <Route
              path="/admin/dashboard"
              element={
                <ProtectedRoute role={['admin', 'developer', 'outreach']}>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/clients"
              element={
                <ProtectedRoute role={['admin', 'developer', 'outreach']}>
                  <AdminClients />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/requests"
              element={
                <ProtectedRoute role={['admin', 'developer', 'outreach']}>
                  <AdminRequests />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/payments"
              element={
                <ProtectedRoute role={['admin', 'developer', 'outreach']}>
                  <AdminPayments />
                </ProtectedRoute>
              }
            />

            {/* Website editor: admin + developer ONLY */}
            <Route
              path="/admin/website-editor"
              element={
                <ProtectedRoute role={['admin', 'developer']}>
                  <AdminWebsiteEditor />
                </ProtectedRoute>
              }
            />

            {/* User management: admin ONLY */}
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute role={['admin']}>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthProvider>
  );
}

export default App;
